import React, { useState } from 'react';

/**
 * InstructionImagesSlider - Displays a collapsible slider of instruction images
 * Shows images in sequence with navigation controls
 */
const InstructionImagesSlider = ({ title, images, isTestMode }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div
      className="mb-3"
      {...(isTestMode && { 'data-testid': 'instruction-images-slider' })}
    >
      {/* Toggle Button */}
      <button
        type="button"
        className="btn btn-link text-primary p-0 text-decoration-none d-flex align-items-center"
        onClick={() => setIsExpanded(!isExpanded)}
        {...(isTestMode && { 'data-testid': 'instruction-toggle-btn' })}
      >
        <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
        <span className="small">[{title}]</span>
      </button>

      {/* Slider Content */}
      {isExpanded && (
        <div
          className="mt-3 border rounded-3 p-3 bg-light"
          {...(isTestMode && { 'data-testid': 'instruction-images-container' })}
        >
          {/* Image Display */}
          <div className="position-relative">
            <img
              src={images[currentIndex]}
              alt={`Instruction step ${currentIndex + 1}`}
              className="img-fluid rounded"
              style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
              {...(isTestMode && { 'data-testid': `instruction-image-${currentIndex}` })}
            />

            {/* Navigation Arrows (only if more than one image) */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className="btn btn-light position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle shadow-sm"
                  onClick={handlePrev}
                  style={{ width: '40px', height: '40px' }}
                  aria-label="Previous image"
                  {...(isTestMode && { 'data-testid': 'instruction-prev-btn' })}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-light position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle shadow-sm"
                  onClick={handleNext}
                  style={{ width: '40px', height: '40px' }}
                  aria-label="Next image"
                  {...(isTestMode && { 'data-testid': 'instruction-next-btn' })}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </>
            )}
          </div>

          {/* Step Indicator */}
          <div className="text-center mt-2">
            <span className="badge bg-secondary">
              {currentIndex + 1} / {images.length}
            </span>
          </div>

          {/* Dot Navigation */}
          {images.length > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`btn p-0 rounded-circle ${index === currentIndex ? 'bg-primary' : 'bg-secondary'}`}
                  style={{ width: '10px', height: '10px', border: 'none' }}
                  onClick={() => handleDotClick(index)}
                  aria-label={`Go to step ${index + 1}`}
                  {...(isTestMode && { 'data-testid': `instruction-dot-${index}` })}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InstructionImagesSlider;
